import { concatInstances } from "./indicatorGeometry";

/**
 * Instances of one kind of plots of an indicator (lines, histograms,
 * backgrounds or markers) in one renderer. Per-segment kinds have an
 * instance between each two bars, the others one per bar. The plots follow
 * each other in the buffer, so a tick rewrites the tail of each in place.
 *
 * @param {object} renderer - a gl renderer: setInstances, updateInstances
 * @param {boolean} isPerSegment
 * @param {(params: object) => Float32Array} build - instances of
 *   { plot, plotIndex, data: { series, bars }, first, count, priceBase }
 */
export const createPlotLayer = (renderer, isPerSegment, build) => {
  let plots = [];
  let count = 0;
  let priceBase = 0;

  return {
    /**
     * @param {object[]} nextPlots - visible plots of this kind (shownPlots)
     * @param {{ series: object, bars: object[] }} data
     * @param {number} nextPriceBase - prices are written relative to it
     */
    rebuild: (nextPlots, data, nextPriceBase) => {
      plots = nextPlots;
      priceBase = nextPriceBase;
      count = isPerSegment ? Math.max(0, data.bars.length - 1) : data.bars.length;
      renderer.setInstances(
        concatInstances(plots.map((plot, plotIndex) => build({ plot, plotIndex, data, first: 0, count, priceBase }))),
      );
    },

    /**
     * Rewrites the instances from `fromIndex` on; the bar count is that of the last rebuild.
     *
     * @param {{ series: object, bars: object[] }} data
     * @param {number} fromIndex - first value that changed
     */
    updateFrom: (data, fromIndex) => {
      const first = Math.max(0, isPerSegment ? fromIndex - 1 : fromIndex);

      if (first >= count) return;

      plots.forEach((plot, plotIndex) => {
        renderer.updateInstances(
          plotIndex * count + first,
          build({ plot, plotIndex, data, first, count: count - first, priceBase }),
        );
      });
    },
  };
};
